import { useMutation } from "@tanstack/react-query";
import { useAuthStore } from "../stores/authStore";
import type {
  LoginData,
  LoginRequest,
  LoginStepData,
  VerifyOtpRequest,
  ResendOtpRequest,
  OtpResendData,
} from "../services/authApi";
import { authApi } from "../services/authApi";
import { useNavigate } from "react-router";
import { extractPermissionsFromToken, extractRolesFromToken } from "@/shared/utils/jwtUtils";

export const useLogin = () => {
   return useMutation<LoginStepData, Error, LoginRequest>({
    mutationFn: async (credentials: LoginRequest) => {
      return await authApi.login(credentials);
    },
  });
};

export const useVerifyOtp = () => {
  const { setAuth } = useAuthStore();

  return useMutation<LoginData, Error, VerifyOtpRequest>({
    mutationFn: async (request: VerifyOtpRequest) => {
      return await authApi.verifyOtp(request);
    },
    onSuccess: (data) => {
      completeLogin(data, setAuth);
    },
  });
};

export const useResendOtp = () => {
  return useMutation<OtpResendData, Error, ResendOtpRequest>({
    mutationFn: async (request: ResendOtpRequest) => {
      return await authApi.resendOtp(request);
    },
  });
};

export function completeLogin(
  data: LoginData,
  setAuth: (
    user: LoginData["user"] & { roles: string[] },
    token: string,
    permissions: string[],
    refreshToken: string,
    expiresAt?: string
  ) => void
) {
  if (!data.token || data.token.trim() === '') {
    throw new Error('No access token received from server');
  }

  const roles = extractRolesFromToken(data.token);
  const userWithRoles = { ...data.user, roles };

  const permissions =
    data.permissions && data.permissions.length > 0
      ? data.permissions
      : extractPermissionsFromToken(data.token);

  setAuth(userWithRoles, data.token, permissions, data.refreshToken, data.expiresAt);
}

export const useLogout = () => {
  const { logout, refreshToken } = useAuthStore();
  const navigate = useNavigate();

  return {
    mutate: () => {
      if (refreshToken) {
        authApi.logout(refreshToken).catch(() => {});
      }
      logout();
      navigate("/login");
    },
  };
};
